import { useState, useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { Check, Crown, Loader2 } from "lucide-react";
import { trackSubscriptionStarted, trackSubscriptionCompleted } from "@/lib/analytics";

const Subscription = () => {
  const { language, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [pollingSessionId, setPollingSessionId] = useState(null);

  useEffect(() => {
    fetchSubscriptionInfo();
    
    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setPollingSessionId(sessionId);
      pollPaymentStatus(sessionId, 0);
    }
  }, []);

  const fetchSubscriptionInfo = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/subscription/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error("Error fetching subscription info:", error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      toast.error(t(
        "No se pudo verificar el pago. Por favor, contacta con soporte.",
        "No s'ha pogut verificar el pagament. Si us plau, contacta amb suport."
      ));
      setPollingSessionId(null);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        trackSubscriptionCompleted(searchParams.get('plan') || 'unknown');
        toast.success(t("¡Suscripción activada con éxito!", "Subscripció activada amb èxit!"));
        setPollingSessionId(null);
        fetchSubscriptionInfo();
        // Redirect to dashboard after 2 seconds
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      } else if (response.data.status === 'expired') {
        toast.error(t("La sesión de pago ha expirado", "La sessió de pagament ha expirat"));
        setPollingSessionId(null);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setPollingSessionId(null);
    }
  };

  const handleSubscribe = async (plan) => {
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const originUrl = window.location.origin;

      const response = await axios.post(`${API}/subscription/create-checkout`, {
        plan: plan,
        origin_url: originUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to Stripe Checkout
      if (response.data.url) {
        trackSubscriptionStarted(plan);
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error(t("Error al crear sesión de pago", "Error en crear sessió de pagament"));
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm(t(
      "¿Estás seguro de que quieres cancelar tu suscripción?",
      "Estàs segur que vols cancel·lar la teva subscripció?"
    ))) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t(
        "Suscripción cancelada. Tendrás acceso hasta el final del período de facturación.",
        "Subscripció cancel·lada. Tindràs accés fins al final del període de facturació."
      ));
      fetchSubscriptionInfo();
    } catch (error) {
      console.error("Error canceling subscription:", error);
      toast.error(t("Error al cancelar suscripción", "Error en cancel·lar subscripció"));
    }
  };

  const t = (es, ca) => language === "ca" ? ca : es;

  const isActive = subscriptionInfo?.status === 'active';
  const isTrial = subscriptionInfo?.status === 'trial';
  const earlyBird = subscriptionInfo?.early_bird_available;
  const earlyBirdRemaining = subscriptionInfo?.early_bird_remaining || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
      <Navigation />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12 fade-in">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 glow-text">
            {t("Tu Suscripción", "La Teva Subscripció")}
          </h1>
          <p className="text-lg text-gray-300">
            {isTrial && t(
              "Estás en período de prueba gratuito (7 días)",
              "Estàs en període de prova gratuït (7 dies)"
            )}
            {isActive && t(
              "Tu suscripción está activa",
              "La teva subscripció està activa"
            )}
          </p>
        </div>

        {/* Polling Status */}
        {pollingSessionId && (
          <div className="mb-8 p-6 rounded-2xl glass border border-teal-400/30 text-center fade-in">
            <Loader2 className="w-8 h-8 text-teal-400 mx-auto mb-3 animate-spin" />
            <p className="text-lg text-teal-300">
              {t("Verificando tu pago...", "Verificant el teu pagament...")}
            </p>
            <p className="text-sm text-gray-400 mt-2">
              {t("Por favor, espera un momento", "Si us plau, espera un moment")}
            </p>
          </div>
        )}

        {/* Subscription Plans */}
        {!isActive && (
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Monthly Plan */}
            <div className="card glass rounded-3xl p-8 border border-purple-400/20 hover:border-purple-400/40 transition-all fade-in">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-purple-200 mb-2">
                  {t("Plan Mensual", "Pla Mensual")}
                </h3>
                {earlyBird ? (
                  <>
                    <div className="text-4xl font-bold text-teal-300 mb-1">
                      7,99€
                      <span className="text-lg text-gray-400">/mes</span>
                    </div>
                    <p className="text-xs text-gray-500 line-through">9,99€/mes</p>
                    <p className="text-xs text-purple-300 font-semibold mt-1">
                      Early Bird — {earlyBirdRemaining} {t("plazas restantes", "places restants")}
                    </p>
                  </>
                ) : (
                  <div className="text-4xl font-bold text-teal-300 mb-2">
                    9,99€
                    <span className="text-lg text-gray-400">/mes</span>
                  </div>
                )}
                <p className="text-sm text-gray-400 mt-2">
                  {t("7 días de prueba gratuita", "7 dies de prova gratuïta")}
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">{t("Acceso completo a 30 lecciones", "Accés complet a 30 lliçons")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">{t("30 ejercicios con feedback IA", "30 exercicis amb feedback IA")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">{t("Feedback personalizado de IA", "Feedback personalitzat d'IA")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">{t("Diario personal con reflexión semanal", "Diari personal amb reflexió setmanal")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">{t("Chat ilimitado con IA", "Xat il·limitat amb IA")}</span>
                </li>
              </ul>

              <button
                onClick={() => handleSubscribe('monthly')}
                disabled={loading}
                className="w-full btn-secondary py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t("Procesando...", "Processant...") : t("Suscribirse", "Subscriure's")}
              </button>
            </div>

            {/* Yearly Plan */}
            <div className="card glass rounded-3xl p-8 border-2 border-purple-400/50 hover:border-purple-400/70 transition-all relative overflow-hidden fade-in">
              {/* Best Value Badge */}
              <div className="absolute top-4 right-4 bg-gradient-to-r from-purple-500 to-purple-700 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                <Crown className="w-3 h-3" />
                {t("MEJOR VALOR", "MILLOR VALOR")}
              </div>

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-purple-200 mb-2">
                  {t("Plan Anual", "Pla Anual")}
                </h3>
                {earlyBird ? (
                  <>
                    <div className="text-4xl font-bold text-teal-300 mb-1">
                      79€
                      <span className="text-lg text-gray-400">/{t("año","any")}</span>
                    </div>
                    <p className="text-xs text-gray-500 line-through">99€/{t("año","any")}</p>
                    <p className="text-sm text-purple-300 font-semibold mt-1">
                      {t("Ahorra 40€ al año", "Estalvia 40€ a l'any")} — Early Bird
                    </p>
                  </>
                ) : (
                  <>
                    <div className="text-4xl font-bold text-teal-300 mb-2">
                      99€
                      <span className="text-lg text-gray-400">/{t("año","any")}</span>
                    </div>
                    <p className="text-sm text-purple-300 font-semibold">
                      {t("Ahorra 20€ al año", "Estalvia 20€ a l'any")}
                    </p>
                  </>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  {t("7 días de prueba gratuita", "7 dies de prova gratuïta")}
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">{t("Acceso completo a 30 lecciones", "Accés complet a 30 lliçons")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">{t("30 ejercicios con feedback IA", "30 exercicis amb feedback IA")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">{t("Feedback personalizado de IA", "Feedback personalitzat d'IA")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">{t("Diario personal con reflexión semanal", "Diari personal amb reflexió setmanal")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">{t("Chat ilimitado con IA", "Xat il·limitat amb IA")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                  <span className="text-purple-300 font-semibold">{t("Acceso prioritario a nuevas funciones", "Accés prioritari a noves funcions")}</span>
                </li>
              </ul>

              <button
                onClick={() => handleSubscribe('yearly')}
                disabled={loading}
                className="w-full btn-primary py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t("Procesando...", "Processant...") : t("Suscribirse", "Subscriure's")}
              </button>
            </div>
          </div>
        )}

        {/* Active Subscription Info */}
        {isActive && subscriptionInfo && (
          <div className="card glass rounded-3xl p-8 mb-8 fade-in">
            <div className="flex items-center gap-4 mb-6">
              <Crown className="w-8 h-8 text-teal-400" />
              <div>
                <h3 className="text-2xl font-bold text-teal-200">
                  {t("Suscripción Activa", "Subscripció Activa")}
                </h3>
                <p className="text-gray-400">
                  {subscriptionInfo.end_date && t(
                    `Válida hasta: ${new Date(subscriptionInfo.end_date).toLocaleDateString()}`,
                    `Vàlida fins: ${new Date(subscriptionInfo.end_date).toLocaleDateString()}`
                  )}
                </p>
              </div>
            </div>

            <button
              onClick={handleCancelSubscription}
              className="btn-secondary px-6 py-3"
            >
              {t("Cancelar Suscripción", "Cancel·lar Subscripció")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscription;
